import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const NavigationContext = createContext(null);
export const useNavigation = () => useContext(NavigationContext);

// Map known app route prefixes to roles and back. The app uses
// routes like `/dashboard`, `/DashboardManager`, `/userprofile`, `/DashboardSuper`, etc.
const roleFromPath = (pathname) => {
  const p = pathname.toLowerCase();
  if (p.startsWith('/dashboardsuper')) return 'super';
  if (p.startsWith('/dashboardmanager')) return 'manager';
  if (p.startsWith('/dashboard')) return 'admin';
  if (p.startsWith('/userprofileadmin')) return 'admin';
  if (p.startsWith('/userprofilemanager')) return 'manager';
  if (p.startsWith('/userprofile')) return 'employee';
  return null;
};

const prefixForRole = {
  admin: 'dashboard',
  manager: 'DashboardManager',
  employee: 'userprofile',
  super: 'DashboardSuper',
};

const routeToPage = (pathname, role) => {
  const prefix = prefixForRole[role] || prefixForRole.employee;
  const re = new RegExp(`^\\/${prefix}`, 'i');
  let cleaned = pathname.replace(re, '');
  cleaned = cleaned.replace(/^\//, '');
  if (!cleaned) return 'home';
  return cleaned.split('/')[0];
};

const pageToRoute = (page, role) => {
  const prefix = prefixForRole[role] || prefixForRole.employee;
  if (!page || page === 'home') return `/${prefix}`;
  return `/${prefix}/${page}`;
};

export function NavigationProvider({ children, role: roleProp, storageKey: storageKeyProp }) {
  const location = useLocation();
  const navigate = useNavigate();

  const inferredRole = roleFromPath(location.pathname);
  const role = roleProp || inferredRole || 'employee';
  const storageKey = storageKeyProp || `nav_${role}`;

  const isInitialMount = useRef(true);
  const writeTimer = useRef(null);

  const [page, setPageState] = useState(() => {
    // start null so we derive from URL on mount
    return null;
  });

  // initialize from URL once
  useEffect(() => {
    const derived = routeToPage(location.pathname, role);
    setPageState(derived);
    isInitialMount.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // sync when location changes (back/forward or navigate)
  useEffect(() => {
    const derived = routeToPage(location.pathname, role);
    setPageState(derived);
  }, [location.pathname, role]);

  // persist to localStorage (debounced), skip initial mount
  useEffect(() => {
    if (isInitialMount.current) return;
    if (writeTimer.current) clearTimeout(writeTimer.current);
    writeTimer.current = setTimeout(() => {
      try {
        localStorage.setItem(storageKey, JSON.stringify({ page, ts: Date.now() }));
      } catch (e) {
        // ignore
      }
    }, 200);
    return () => clearTimeout(writeTimer.current);
  }, [page, storageKey]);

  const navigateTo = useCallback((targetPage, options = { replace: false }) => {
    const route = pageToRoute(targetPage, role);
    navigate(route, { replace: options.replace });
    // optimistic
    setPageState(targetPage);
  }, [navigate, role]);

  const resetNavigation = useCallback(() => {
    try { localStorage.removeItem(storageKey); } catch (e) {}
    setPageState('home');
    navigate(pageToRoute('home', role), { replace: true });
  }, [navigate, role, storageKey]);

  return (
    <NavigationContext.Provider value={{ page, setPage: navigateTo, navigateTo, resetNavigation, role }}>
      {children}
    </NavigationContext.Provider>
  );
}

export default NavigationContext;
